import { maskVendor } from "@/shared/lib/vendorMask";
import { useQuery } from "@tanstack/react-query";
import { AlertCircle, Compass, Loader2 } from "lucide-react";
import { useState } from "react";

import { label, SKU } from "@/entities/pricing/model/priceBook";
import { usePriceBook } from "@/entities/pricing/model/usePriceBook";
import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import { Card, CardContent } from "@/shared/ui/card";
import { QueryErrorBanner } from "@/shared/ui/QueryErrorBanner";

import { bizAdvisorService, parseDiagnosis } from "../api/bizAdvisorService";
import type { DiagnoseInputs, RemeasureResult } from "../api/bizAdvisorService";
import { useDiagnosisPolling } from "../model/useDiagnosisPolling";
import { DiagnosisPanel } from "./components/DiagnosisPanel";
import { RemeasureDeltaCard } from "./components/RemeasureDeltaCard";
import { errorText } from "@/shared/lib/apiErrors";

/** 跑一次新诊断并轮询到终态(复测用,复用同一手填增强);返回新诊断 id(SUCCESS)或抛错。 */
async function runDiagnosisToSuccess(input: DiagnoseInputs): Promise<number> {
  const res = await bizAdvisorService.createDiagnosis(input);
  const newId = res.id;
  for (let i = 0; i < 60; i++) {
    await new Promise((r) => setTimeout(r, 3000));
    const d = await bizAdvisorService.getDiagnosis(newId);
    if (d.status === "SUCCESS") return newId;
    if (d.status === "FAILED") throw new Error(maskVendor(d.errorMessage) ?? "复测诊断失败");
  }
  throw new Error("复测诊断超时");
}

const ACTIVE = new Set(["RUNNING"]);

/** 把「逗号/顿号/换行」分隔的竞品字符串解析成去空数组。 */
function parseCompetitors(raw: string): string[] {
  return raw
    .split(/[,，、\n]/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function StatusBadge({ status }: { status: string }) {
  if (status === "SUCCESS") return <Badge className="bg-emerald-500 text-white">完成</Badge>;
  if (status === "FAILED") return <Badge variant="destructive">失败</Badge>;
  return <Badge variant="secondary">诊断中</Badge>;
}

export default function BizAdvisorPage() {
  const [brandHint, setBrandHint] = useState("");
  const [industry, setIndustry] = useState("");
  const [competitors, setCompetitors] = useState("");
  const [benchmark, setBenchmark] = useState("");
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);
  const [remeasure, setRemeasure] = useState<RemeasureResult | null>(null);
  const [remeasuring, setRemeasuring] = useState(false);
  const [remeasureError, setRemeasureError] = useState<string | null>(null);
  // 价签来自后端价目表(0903 起 advisor.diagnosis 是 LIST 固定价);取不到价不编数字
  const { book } = usePriceBook();
  const diagnosisCredits = book.unit(SKU.advisorDiagnosis, "default");

  const listQuery = useQuery({
    queryKey: ["biz-advisor", "diagnoses"],
    queryFn: () => bizAdvisorService.listDiagnoses(20),
    refetchInterval: (q) => ((q.state.data ?? []).some((d) => ACTIVE.has(d.status)) ? 3000 : false),
    meta: { suppressGlobalErrorToast: true },
  });

  const { diagnosis } = useDiagnosisPolling(selectedId);

  /** 当前手填增强入参(发起 + 复测复用)。 */
  const buildInputs = (): DiagnoseInputs => ({
    brandHint: brandHint.trim(),
    industry: industry.trim(),
    competitors: parseCompetitors(competitors),
    benchmark: benchmark.trim(),
  });

  const start = async () => {
    setStarting(true);
    setStartError(null);
    try {
      const res = await bizAdvisorService.createDiagnosis(buildInputs());
      setSelectedId(res.id);
      void listQuery.refetch();
    } catch (e: unknown) {
      setStartError(errorText(e));
    } finally {
      setStarting(false);
    }
  };

  /** 复测:跑一次新诊断(扣 biz_advisor 积分)→ 算被派乘子 delta(锚此次诊断的派单)。 */
  const doRemeasure = async () => {
    if (!selectedId) return;
    setRemeasuring(true);
    setRemeasureError(null);
    setRemeasure(null);
    try {
      const newId = await runDiagnosisToSuccess(buildInputs());
      const res = await bizAdvisorService.remeasureResult(selectedId, newId);
      setRemeasure(res);
      void listQuery.refetch();
    } catch (e: unknown) {
      setRemeasureError(errorText(e));
    } finally {
      setRemeasuring(false);
    }
  };

  const report =
    diagnosis && diagnosis.status === "SUCCESS" ? parseDiagnosis(diagnosis.reportJson) : null;
  const items = listQuery.data ?? [];

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <Compass className="h-6 w-6 text-primary" />
        <h1 className="text-2xl font-semibold">经营诊断</h1>
        <span className="text-sm text-muted-foreground">用你的真实经营数据做一次经营能力体检</span>
      </div>

      <Card>
        <CardContent className="space-y-4 p-6">
          <div className="flex items-end gap-3">
            <div className="flex-1">
              <label htmlFor="biz-brand" className="text-sm text-muted-foreground">
                品牌(可空,留空则用最近一次GEO雷达的品牌)
              </label>
              <input
                id="biz-brand"
                className="mt-1 w-full rounded-md border bg-background px-3 py-2 text-sm"
                placeholder="如:你的品牌名"
                value={brandHint}
                onChange={(e) => setBrandHint(e.target.value)}
              />
            </div>
            <Button data-testid="biz-start" onClick={() => void start()} disabled={starting}>
              {starting ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Compass className="mr-2 h-4 w-4" />
              )}
              发起诊断
              {label(diagnosisCredits, (c) => ` · ${c} 积分/次`)}
            </Button>
          </div>

          <details data-testid="biz-advanced" className="group rounded-md border bg-muted/30">
            <summary className="cursor-pointer select-none px-3 py-2 text-sm font-medium text-muted-foreground">
              手填增强(可选)— 行业 / 竞品 / 对标标杆
            </summary>
            <div className="grid gap-3 px-3 pb-3 sm:grid-cols-3">
              <div>
                <label htmlFor="biz-industry" className="text-xs text-muted-foreground">
                  行业(留空则读经营数据派生)
                </label>
                <input
                  id="biz-industry"
                  data-testid="biz-industry"
                  className="mt-1 w-full rounded-md border bg-background px-3 py-2 text-sm"
                  placeholder="如:商标转让"
                  value={industry}
                  onChange={(e) => setIndustry(e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="biz-competitors" className="text-xs text-muted-foreground">
                  已知竞品(逗号分隔,结合竞争态势)
                </label>
                <input
                  id="biz-competitors"
                  data-testid="biz-competitors"
                  className="mt-1 w-full rounded-md border bg-background px-3 py-2 text-sm"
                  placeholder="如:权大师,猪八戒"
                  value={competitors}
                  onChange={(e) => setCompetitors(e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="biz-benchmark" className="text-xs text-muted-foreground">
                  对标标杆(打分参照其经营水位)
                </label>
                <input
                  id="biz-benchmark"
                  data-testid="biz-benchmark"
                  className="mt-1 w-full rounded-md border bg-background px-3 py-2 text-sm"
                  placeholder="如:行业头部品牌"
                  value={benchmark}
                  onChange={(e) => setBenchmark(e.target.value)}
                />
              </div>
            </div>
          </details>
        </CardContent>
      </Card>

      {startError && (
        <div className="flex items-center gap-2 rounded-md bg-rose-50 px-4 py-2 text-sm text-rose-700">
          <AlertCircle className="h-4 w-4" /> {startError}
        </div>
      )}

      {selectedId && (
        <div data-testid="biz-diagnosis-detail">
          {diagnosis?.status === "RUNNING" && (
            <Card>
              <CardContent className="flex items-center gap-3 p-6 text-sm text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin" /> 诊断中(读取经营数据 + 五乘子分析)…
              </CardContent>
            </Card>
          )}
          {diagnosis?.status === "FAILED" && (
            <Card>
              <CardContent className="p-6 text-sm text-rose-600">
                诊断失败:{maskVendor(diagnosis.errorMessage) ?? "未知错误"}
              </CardContent>
            </Card>
          )}
          {report && <DiagnosisPanel diagnosis={report} diagnosisId={selectedId} />}

          {report && (
            <Card>
              <CardContent className="flex flex-wrap items-center gap-3 p-4 text-sm">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => void doRemeasure()}
                  disabled={remeasuring}
                >
                  {remeasuring ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  复测对比
                </Button>
                <span className="text-muted-foreground">
                  复测会重新跑一次诊断{label(diagnosisCredits, (c) => `(扣 ${c} 积分)`)}
                  ,对比被派乘子的变化。
                </span>
              </CardContent>
            </Card>
          )}
          {remeasureError && (
            <div className="flex items-center gap-2 rounded-md bg-rose-50 px-4 py-2 text-sm text-rose-700">
              <AlertCircle className="h-4 w-4" /> {remeasureError}
            </div>
          )}
          {remeasure && <RemeasureDeltaCard result={remeasure} />}
        </div>
      )}

      <Card>
        <CardContent className="p-6">
          <div className="mb-3 text-sm font-medium">最近诊断</div>
          <div className="space-y-1">
            {/* audit-0713 C1b:加载/错误不再伪装成「暂无记录」 */}
            {listQuery.isLoading && <div className="text-sm text-muted-foreground">加载中…</div>}
            <QueryErrorBanner
              isError={listQuery.isError}
              onRetry={() => void listQuery.refetch()}
            />
            {!listQuery.isLoading && !listQuery.isError && items.length === 0 && (
              <div className="text-sm text-muted-foreground">暂无记录</div>
            )}
            {items.map((d) => (
              <button
                key={d.id}
                onClick={() => {
                  setSelectedId(d.id);
                  setRemeasure(null);
                  setRemeasureError(null);
                }}
                className={`flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm hover:bg-muted ${
                  selectedId === d.id ? "bg-muted" : ""
                }`}
              >
                <span className="truncate">{d.brand || "(识别中)"}</span>
                <span className="flex items-center gap-2">
                  <StatusBadge status={d.status} />
                  {d.status === "SUCCESS" && d.overallScore != null && (
                    <span className="tabular-nums text-muted-foreground">{d.overallScore}</span>
                  )}
                </span>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
